import React, { useState, useEffect, useCallback, useRef } from 'react';
import StatsGrid from '../components/StatsGrid';
import RevenueChart from '../components/RevenueChart';
import TopProducts from '../components/TopProducts';
import AppDeploymentStatus from '../components/AppDeploymentStatus';
import ActivityFeed from '../components/ActivityFeed';
import { toast } from 'react-toastify';
import { getWebSocketManager } from '../services/websocket';
import { API_CONFIG, getWebSocketURL } from '../config/api';
import useDashboardStore from '../store/dashboardStore';
import { api } from '../api/client';
import logger from '../utils/logger';
import { isNativeApp } from '../config';

const Dashboard = () => {
  const updateFromMetrics = useDashboardStore((state) => state.updateFromMetrics);
  const updateRevenueSeries = useDashboardStore((state) => state.updateRevenueSeries);
  const updateTopProductsStore = useDashboardStore((state) => state.updateTopProducts);
  const loadDemoData = useDashboardStore((state) => state.loadDemoData);
  const storeDemoMode = useDashboardStore((state) => state.demoMode);
  const useLocalData = isNativeApp || storeDemoMode;

  // State for real-time data not covered by store
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const wsManagerRef = useRef(null);

  // Fetch initial data - moved before usage in useEffect
  const fetchInitialData = useCallback(async () => {
    if (useLocalData) {
      return;
    }
    try {
      // Fetch dashboard metrics
      const metrics = await api.metrics();
      updateFromMetrics(metrics);
      setLastUpdated(new Date());

      // Fetch recent alerts (non-blocking)
      api.alertsRecent()
        .then((alerts) => {
          if (Array.isArray(alerts)) {
            // Optional: toast first critical alert
            const critical = alerts.find(a => a.level === 'error' || a.level === 'critical');
            if (critical) toast.error(critical.message || 'System alert');
          }
        })
        .catch(() => {});
    } catch (error) {
      logger.error('Failed to fetch initial dashboard data:', error);
      toast.error('Failed to load dashboard data');
    }
  }, [useLocalData, updateFromMetrics]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (useLocalData || !API_CONFIG.features.websockets) {
      logger.info('WebSocket disabled via configuration');
      return;
    }

    const wsUrl = getWebSocketURL();
    const manager = getWebSocketManager(wsUrl);
    const token = localStorage.getItem('auth_token');

    wsManagerRef.current = manager;

    // Connect to WebSocket
    manager.connect(token);

    // Set up listeners for connection state changes
    const unsubscribeConnectionState = manager.on('connectionState', ({ state, details }) => {
      setConnectionStatus(state);

      if (state === 'connected') {
        toast.success('Connected to live updates');
      } else if (state === 'error') {
        if (details?.reason?.includes('rate limit') || details?.reason?.includes('429')) {
          toast.warning('Rate limited - using offline data');
        } else {
          toast.error('Connection error - using offline data');
        }
      }
    });

    // Set up listeners for real-time updates
    const unsubscribeStats = manager.on('dashboard_stats', (data) => {
      logger.debug('Received dashboard stats update:', data);
      updateFromMetrics(data);
      setLastUpdated(new Date());

      // Show notification for significant changes
      if (data.revenue_change > 10) {
        toast.success(`Revenue increased by ${data.revenue_change}%!`);
      }
    });

    const unsubscribeRevenue = manager.on('revenue_updates', (data) => {
      logger.debug('Received revenue update:', data);
      updateRevenueSeries(data);
      setLastUpdated(new Date());
    });

    const unsubscribeProducts = manager.on('product_updates', (data) => {
      logger.debug('Received product update:', data);
      const productsPayload = Array.isArray(data?.products)
        ? data.products
        : Array.isArray(data)
          ? data
          : data?.top_products;

      if (Array.isArray(productsPayload) && productsPayload.length) {
        updateTopProductsStore(productsPayload, {
          source: 'realtime',
          updatedAt: new Date().toISOString(),
        });
        setLastUpdated(new Date());
      }
    });

    const unsubscribeAlerts = manager.on('alert_notifications', (alert) => {
      logger.debug('Received alert:', alert);

      // Show alert based on type
      switch(alert.type) {
        case 'price_drop':
          toast.info(`Price drop alert: ${alert.product} now ${alert.price}`);
          break;
        case 'low_stock':
          toast.warning(`Low stock alert: ${alert.product} has ${alert.quantity} units left`);
          break;
        case 'new_competitor':
          toast.info(`New competitor detected: ${alert.competitor} selling ${alert.product}`);
          break;
        case 'system':
          toast.error(alert.message);
          break;
        default:
          toast(alert.message);
      }
    });

    // Cleanup on unmount
    return () => {
      unsubscribeConnectionState();
      unsubscribeStats();
      unsubscribeRevenue();
      unsubscribeProducts();
      unsubscribeAlerts();
      if (wsManagerRef.current === manager) {
        wsManagerRef.current = null;
      }
    };
  }, [useLocalData, updateFromMetrics, updateRevenueSeries, updateTopProductsStore]);

  // Fetch initial data on mount
  useEffect(() => {
    let mounted = true;
    if (!useLocalData) {
      // Defer execution to avoid synchronous state updates during render/effect cycles
      const timer = setTimeout(() => {
        if (mounted) fetchInitialData();
      }, 0);
      return () => {
        mounted = false;
        clearTimeout(timer);
      };
    }
  }, [useLocalData, fetchInitialData]);

  // Manual refresh function
  const handleRefresh = useCallback(() => {
    if (useLocalData) {
      loadDemoData();
      setLastUpdated(new Date());
      toast.info('Dashboard data refreshed');
      return;
    }

    fetchInitialData();
    toast.info('Dashboard refreshed');
  }, [fetchInitialData, loadDemoData, useLocalData]);

  // Force reconnect function
  const handleReconnect = useCallback(() => {
    if (useLocalData) {
      return;
    }

    if (wsManagerRef.current) {
      const token = localStorage.getItem('auth_token');
      wsManagerRef.current.connect(token);
      toast.info('Reconnecting to live updates...');
    }
  }, [useLocalData]);

  const statusClass = useLocalData ? 'connected' : connectionStatus;
  const statusText = useLocalData
    ? 'Review Data'
    : connectionStatus === 'connected'
      ? 'Live'
      : connectionStatus === 'connecting'
        ? 'Connecting...'
        : 'Offline';

  return (
    <div className="dashboard-container">
      {/* Connection Status Bar */}
      <div className="connection-status">
        <div className={`status-indicator ${statusClass}`}>
          <span className="status-dot"></span>
          <span className="status-text">{statusText}</span>
        </div>
        <div className="last-updated">
          Last updated: {lastUpdated.toLocaleTimeString()}
        </div>
        <div className="dashboard-actions">
          <button onClick={handleRefresh} className="btn-refresh" title="Refresh Data">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
          </button>
          {!useLocalData && connectionStatus !== 'connected' && (
            <button onClick={handleReconnect} className="btn-reconnect" title="Reconnect">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Real-time Stats Grid */}
      <StatsGrid />

      {/* Charts and Products - using CSS class for responsive grid */}
      <div className="dashboard-grid">
        <RevenueChart />
        <TopProducts />
      </div>

      {/* App Deployment Status Widget */}
      <AppDeploymentStatus />

      {/* Ecosystem Activity Feed -- compact widget showing last 8 items */}
      <div style={{ marginTop: '24px' }}>
        <ActivityFeed compact={true} maxItems={8} />
      </div>

      <style>{`
        .dashboard-container {
          position: relative;
          padding: 20px;
        }

        .connection-status {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px;
          background: linear-gradient(135deg, rgba(0, 255, 255, 0.05), rgba(255, 0, 255, 0.05));
          border: 1px solid rgba(0, 255, 255, 0.2);
          border-radius: 8px;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 8px;
        }

        .status-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        .status-indicator.connected .status-dot {
          background: #00ff00;
          box-shadow: 0 0 10px #00ff00;
        }

        .status-indicator.connecting .status-dot {
          background: #ffaa00;
          box-shadow: 0 0 10px #ffaa00;
        }

        .status-indicator.disconnected .status-dot {
          background: #ff4444;
          box-shadow: 0 0 10px #ff4444;
        }

        .status-text {
          font-weight: 600;
          font-size: 14px;
        }

        .last-updated {
          color: #888;
          font-size: 13px;
        }

        .dashboard-actions {
          display: flex;
          gap: 10px;
        }

        .btn-refresh, .btn-reconnect {
          padding: 6px 12px;
          background: rgba(0, 255, 255, 0.1);
          border: 1px solid rgba(0, 255, 255, 0.3);
          border-radius: 4px;
          color: #00ffff;
          cursor: pointer;
          transition: all 0.2s;
          min-width: 44px;
          min-height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-refresh:hover, .btn-reconnect:hover {
          background: rgba(0, 255, 255, 0.2);
          transform: scale(1.05);
        }

        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.5;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Responsive dashboard container */
        @media (max-width: 767px) {
          .dashboard-container {
            padding: 8px;
          }

          .connection-status {
            padding: 10px 14px;
            font-size: 12px;
            flex-direction: column;
            align-items: flex-start;
          }

          .last-updated {
            font-size: 11px;
          }

          .status-text {
            font-size: 12px;
          }

          .dashboard-actions {
            width: 100%;
            justify-content: flex-end;
          }
        }

        @media (max-width: 375px) {
          .dashboard-container {
            padding: 4px;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
